/**
 * Premium Auth Page (T+34 — Login Upgrade)
 *
 * Features:
 *  • Google login (primary)
 *  • Apple login
 *  • Continue with phone (gated by PHONE_LOGIN_ENABLED + backend /auth/phone/status)
 *  • Email + password (login / signup)
 *  • Forgot password link → reset-request screen
 *  • "Keep me logged in" toggle (long vs session-only persistence)
 *  • Session verification post-signup/login via GET /auth/me
 *  • Role-based redirect: admin → /admin · vendor → /vendor · customer → /dashboard
 *
 * Hard rules honored:
 *  • All inputs carry data-auth-input="true" → CSS defensive layer
 *    in `index.css` neutralizes autofill / placeholder overlap.
 *  • Phone login UI surfaces "almost ready" banner when A2P pending.
 *  • Forgot password returns generic success — never leaks whether
 *    the email exists.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, apiClient } from "@/App";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, User,
  ChevronLeft, Droplets, UserCircle, Phone, KeyRound,
  CheckCircle2, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import LanguageSelector from "@/components/shared/LanguageSelector";
import { serializeAuthDiagnostic, copyAuthDiagnostic } from "@/components/auth/_authDiagnostics";
import { ThemeToggleCompact } from "@/components/shared/ThemeToggle";
import { PHONE_LOGIN_ENABLED, KEEP_ME_LOGGED_IN_DEFAULT, PASSKEYS_ENABLED } from "@/config/launchFlags";
import { passkeysAvailable, loginWithPasskey } from "@/lib/passkeyClient";
import { CUSTOMER_SMS_CONSENT_TEXT } from "@/lib/smsConsentTexts";
import { Fingerprint } from "lucide-react";
import { verifyLoginSession } from "@/utils/verifyLoginSession";
import { readReturnPath } from "@/utils/safeReturnPath";

const KEEP_LOGGED_IN_LS_KEY = "wp_keep_logged_in";

// Storage helpers — when keep-me-logged-in is unchecked we route
// session_token into sessionStorage (cleared on tab close) instead of
// localStorage.  Persisted preference itself sits in localStorage.
function setKeepLoggedInPref(value) {
  try {
    localStorage.setItem(KEEP_LOGGED_IN_LS_KEY, value ? "1" : "0");
  } catch { /* private mode */ }
}
function readKeepLoggedInPref() {
  try {
    const v = localStorage.getItem(KEEP_LOGGED_IN_LS_KEY);
    if (v === "0") return false;
    if (v === "1") return true;
  } catch { /* private mode */ }
  return KEEP_ME_LOGGED_IN_DEFAULT;
}
function storeSessionTokenSafely(token, keepLoggedIn) {
  // Mirror token into BOTH storages so the existing AuthProvider /
  // protected-route plumbing keeps working.  If the user unchecked
  // "keep me logged in", we additionally tag the localStorage entry
  // so the AuthProvider can clear it on logout AND a tab-close hook
  // also clears it (see below).
  try {
    sessionStorage.setItem("session_token", token);
    if (keepLoggedIn) {
      localStorage.setItem("session_token", token);
    } else {
      // Don't persist token in localStorage when keep-logged-in is off
      localStorage.removeItem("session_token");
    }
  } catch { /* private mode */ }
}


// E.164 normalization — best-effort, no SMS gets sent unless backend
// reports phone_login_enabled=true so this stays a UX nicety.
function normalizeE164(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  // Default to +1 (US/CA) when 10 digits provided
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits ? `+${digits}` : "";
}

// OTA 1.0.3 — Safe-to-show backend host for the auth diagnostic panel.
// Returns just the hostname (no path, no token, no query) so the user
// can see if they're hitting preview vs prod backend on the native shell.
function safeBackendHost() {
  try {
    const raw = process.env.REACT_APP_BACKEND_URL || "";
    if (!raw) return "(unset)";
    const u = new URL(raw);
    return u.host;
  } catch {
    return "(invalid)";
  }
}


const PremiumAuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setSessionToken, login } = useAuth();
  const redirectTo = readReturnPath(searchParams.get("redirect"));

  // Phase 7g.15.66 (2026-02): allow deep-linking directly into signup or
  // forgot-password by reading `?mode=` from the URL. The SMS Consent
  // page's "Customer account registration" link uses `/auth?mode=signup`.
  const initialMode = (() => {
    const m = (searchParams.get("mode") || "").toLowerCase();
    if (m === "signup" || m === "register") return "email_signup";
    if (m === "login") return "email_login";
    if (m === "phone") return "phone";
    if (m === "forgot") return "forgot";
    return "main";
  })();
  const [mode, setMode] = useState(initialMode); // main | email_login | email_signup | phone | forgot
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(readKeepLoggedInPref());
  // Phase 7g.15.48 (Rick 2026-05-27, second pass): UNCHECKED, OPTIONAL
  // SMS consent on customer registration. Persisted on signup payload.
  const [smsConsent, setSmsConsent] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
  });
  const [error, setError] = useState(null);
  // Phase 7g.15.103 — classify login failures so the UI can offer a magic-link
  // recovery path when the request layer (network/timeout/5xx) drops the
  // response. "" = no error, "network" = no response received,
  // "server" = HTTP 5xx, "client" = HTTP 4xx (shown as plain detail string).
  const [errorKind, setErrorKind] = useState("");
  // OTA 1.0.3 (2026-06-19) — Native-safe diagnostic surface. When auth
  // fails on the Android/iOS WebView, the user (and we) lose all
  // visibility because the only signal is the toast string. This
  // captures the WireFrame of the failure (HTTP status, endpoint path,
  // backend host, axios error code/name, detail string, request id,
  // timestamp) so the user can tap "Show details" and read the exact
  // technical cause — and screenshot it for support if needed.
  // STRICTLY sanitized: no token, no password, no cookie, no auth
  // header, no PII. Only metadata the user already typed (email) +
  // server-published diagnostics.
  const [errorDetails, setErrorDetails] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  // OTA 1.0.3 — Copy-to-clipboard feedback state for the diagnostic
  // panel. Transitions: idle → copied (~1.6s) → idle. "failed" surfaces
  // when navigator.clipboard is unavailable AND the execCommand
  // fallback throws (rare on modern Capacitor WebView but possible).
  const [copyStatus, setCopyStatus] = useState("idle"); // idle | copied | failed
  const [magicRecoveryBusy, setMagicRecoveryBusy] = useState(false);
  const [magicRecoverySent, setMagicRecoverySent] = useState(false);
  const [showAppleSetup, setShowAppleSetup] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState({ enabled: PHONE_LOGIN_ENABLED, message: "" });
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  // Poll backend phone-login status on mount so we honor the BE flag
  // even if the FE env flag is wrong (BE is source of truth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get("/auth/phone/status");
        if (!cancelled && r?.data) {
          setPhoneStatus({
            enabled: PHONE_LOGIN_ENABLED && !!r.data.phone_login_enabled,
            message: r.data.message || "",
          });
        }
      } catch {
        // Silent — fall back to FE flag
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Passkey availability — combines FE flag + BE flag + browser support.
  // Only when ALL three are true do we render the passkey button.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!PASSKEYS_ENABLED) return;
      const status = await passkeysAvailable();
      if (!cancelled) setPasskeyReady(!!status.available);
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePasskeyLogin = async () => {
    setPasskeyBusy(true);
    setError(null);
    try {
      const resp = await loginWithPasskey({ keepLoggedIn });
      if (resp?.success) {
        await completeLoginAndRedirect(resp);
      } else {
        setError(t("auth_errors.passkey_signin_incomplete"));
      }
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") {
        // User cancelled — silent
      } else {
        setError(err?.response?.data?.detail || t("auth_errors.passkey_signin_incomplete"));
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleGoogleLogin = () => login();

  const handleAppleLogin = async () => {
    const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    if (!appleClientId) {
      setShowAppleSetup(true);
      return;
    }
    try {
      if (!window.AppleID) {
        const script = document.createElement("script");
        script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
        script.async = true;
        document.body.appendChild(script);
        await new Promise((resolve) => { script.onload = resolve; });
      }
      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: "email name",
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const res = await apiClient.post("/auth/apple/signin", {
        id_token: response.authorization.id_token,
        code: response.authorization.code,
        email: response.user?.email,
        firstName: response.user?.name?.firstName,
        lastName: response.user?.name?.lastName,
      });
      if (res.data.success) {
        await completeLoginAndRedirect(res.data);
      }
    } catch (err) {
      if (err.error === "popup_closed_by_user") return;
      console.error("[apple-signin] error", err);
      toast.error(t("auth_errors.apple_signin_failed"));
    }
  };

  const handleGuestContinue = () => navigate("/book");

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  /**
   * After we have a session_token from any path (signup, login, apple,
   * phone), verify it against /auth/me so we trust the role before we
   * route to /admin vs /vendor vs /dashboard. Prevents bounce-back to
   * /welcome on fresh signups (P0 fix from T+2 hardened).
   */
  async function completeLoginAndRedirect(loginResp) {
    const { user: verifiedUser, token } = await verifyLoginSession(apiClient, loginResp);
    storeSessionTokenSafely(token, keepLoggedIn);
    setKeepLoggedInPref(keepLoggedIn);
    setUser(verifiedUser, true);
    setSessionToken(token);
    // Optional hints must not turn an already verified login into an error.
    try {
      sessionStorage.setItem("login_timestamp", Date.now().toString());
      sessionStorage.removeItem("intendedPath");
    } catch { /* optional hints */ }
    try { localStorage.removeItem("intendedPath"); } catch { /* optional hint */ }

    // Phase 7g.15.138 (2026-06-20, P0 Android auth hardening per Rick directive) —
    // On native Capacitor, the sessionStorage.setItem above is wrapped
    // by bootstrapNative() to also mirror to Capacitor Preferences via
    // an async Preferences.set(...). React Router's navigate() below is
    // soft (no JS-context teardown), but if the user force-closes the
    // app within ~50ms of login (Android task-killer / battery saver),
    // the Preferences write may not finish before the process dies →
    // next cold start hydrates session_token=null → blank screen.
    // Awaiting the flush here makes the login a durable checkpoint.
    try {
      const platformMod = await import("@/native/platform");
      if (platformMod?.isNative?.()) {
        const bootstrapMod = await import("@/native/bootstrap");
        await bootstrapMod.flushNativePreferences?.();
      }
    } catch (_e) { /* native flush is a safety net; never block login */ }

    const role = verifiedUser?.role;
    let target = redirectTo;
    if (["admin", "master_admin", "super_admin"].includes(role) && target === "/dashboard") {
      target = "/admin";
    } else if (role === "vendor" && target === "/dashboard") {
      target = "/vendor";
    }
    toast.success(mode === "email_signup" ? t("auth.create_account") : t("auth.welcome_back"));
    navigate(target, { replace: true });
  }


  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.email.includes("@")) {
      setError(t("auth_errors.invalid_email"));
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setError(t("auth_errors.password_too_short"));
      return;
    }
    if (mode === "email_signup") {
      if (!formData.name.trim()) {
        setError(t("auth_errors.enter_name"));
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError(t("auth_errors.passwords_no_match"));
        return;
      }
    }
    setLoading(true);
    setError(null);
    setErrorKind("");
    setErrorDetails(null);
    setShowErrorDetails(false);
    setCopyStatus("idle");
    setMagicRecoverySent(false);
    try {
      const endpoint = mode === "email_signup" ? "/auth/email/signup" : "/auth/email/login";
      const payload = mode === "email_signup"
        ? {
            ...formData,
            user_type: "customer",
            sms_consent: smsConsent,
            sms_consent_text: smsConsent ? CUSTOMER_SMS_CONSENT_TEXT : "",
            sms_consent_source: "registration",
          }
        : { email: formData.email, password: formData.password };
      const response = await apiClient.post(endpoint, payload);
      if (response.data.success || response.data.session_token) {
        await completeLoginAndRedirect(response.data);
      } else {
        setError(response.data.detail || t("auth_errors.auth_failed"));
        setErrorKind("client");
        // OTA 1.0.3 — Capture 2xx-without-success-envelope diagnostic
        // (rare but possible if the backend response shape regresses
        // or a CDN cache strips the body). No tokens, no PII.
        setErrorDetails({
          status: response.status,
          endpoint,
          backendHost: safeBackendHost(),
          code: response.data?.code || null,
          detail: typeof response.data?.detail === "string" ? response.data.detail : null,
          requestId: response.headers?.["x-request-id"] || response.headers?.["cf-ray"] || null,
          ts: new Date().toISOString(),
        });
      }
    } catch (err) {
      // Phase 7g.15.103 — classify so the UI can show a magic-link recovery
      // action when the request layer dropped the response (the previous
      // "Something went wrong" generic toast left users stuck on native).
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Structured console diagnostic so future reports are one paste away.
      try {
        console.error("[auth] email-auth failed", {
          status: status ?? null,
          code: err?.code ?? null,
          message: err?.message ?? null,
          mode,
          hasResponse: !!err?.response,
          ts: new Date().toISOString(),
        });
      } catch (e) { void e; /* console may be unavailable */ }
      // OTA 1.0.3 — Capture the technical envelope of the failure so
      // the user (and support) can see status + endpoint + backend host
      // + sanitized detail. SAFE: never include password / token /
      // Authorization header / Set-Cookie / full URL with query.
      const endpoint = mode === "email_signup" ? "/auth/email/signup" : "/auth/email/login";
      setErrorDetails({
        status: status ?? null,
        endpoint,
        backendHost: safeBackendHost(),
        code: err?.code ?? null,
        name: err?.name ?? null,
        detail: typeof detail === "string"
          ? detail
          : (detail && typeof detail === "object" && typeof detail.message === "string"
              ? detail.message
              : null),
        requestId: err?.response?.headers?.["x-request-id"] || err?.response?.headers?.["cf-ray"] || null,
        ts: new Date().toISOString(),
      });
      if (!err?.response) {
        // No response received — likely network drop / timeout / WebView abort.
        setError(t("auth_errors.network_dropped"));
        setErrorKind("network");
      } else if (status >= 500) {
        setError(t("auth_errors.server_slow"));
        setErrorKind("server");
      } else {
        // 4xx with a clear server message (e.g., wrong password, account-locked).
        const message = (typeof detail === "string" && detail) || t("auth_errors.auth_failed");
        setError(message);
        setErrorKind("client");
      }
    } finally {
      setLoading(false);
    }
  };

  // Phase 7g.15.103 — Inline magic-link recovery used when login can't
  // reach the backend (network drop / 5xx). Uses the existing
  // /auth/magic-link/send endpoint — no new endpoint, no password reset,
  // no DB writes outside the standard transactional magic_links row.
  const handleEmailMagicLinkRecovery = async () => {
    if (!formData.email || !formData.email.includes("@")) {
      setError(t("auth_errors.invalid_email"));
      return;
    }
    setMagicRecoveryBusy(true);
    try {
      await apiClient.post("/auth/magic-link/send", {
        email: formData.email,
        user_type: "customer",
      });
      setMagicRecoverySent(true);
      setError(null);
      setErrorKind("");
      toast.success(t("auth_errors.magic_link_sent_inline"));
    } catch (mErr) {
      try {
        console.error("[auth] magic-link recovery failed", {
          status: mErr?.response?.status ?? null,
          code: mErr?.code ?? null,
          ts: new Date().toISOString(),
        });
      } catch (e) { void e; /* console may be unavailable */ }
      // Keep the existing error visible — recovery itself failed, so don't
      // overwrite the user's context. Just surface a single toast.
      toast.error(t("auth_errors.could_not_send_link"));
    } finally {
      setMagicRecoveryBusy(false);
    }
  };


  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.email.includes("@")) {
      setError(t("auth_errors.invalid_email"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await apiClient.post("/auth/password/reset-request", { email: formData.email });
    } catch {
      // Intentionally swallow — generic success message even on error
      // so we never leak whether the email exists.
    } finally {
      setLoading(false);
      setForgotSent(true);
    }
  };


  const handlePhoneRequestOtp = async (e) => {
    e.preventDefault();
    const e164 = normalizeE164(formData.phone);
    if (!e164 || e164.length < 8) {
      setError(t("auth_errors.invalid_phone"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post("/auth/phone/send-code", {
        phone: e164,
        user_type: "customer",
      });
      if (res.data?.success) {
        toast.success(t("auth_errors.code_sent"));
      } else {
        setError(res.data?.message || t("auth_errors.could_not_send_code"));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns { phone_login_enabled, message, reason } as detail
      // when the flag is off (503).
      const msg = (detail && typeof detail === "object" && detail.message)
        || (typeof detail === "string" ? detail : null)
        || t("auth_errors.phone_login_pending");
      setError(msg);
      setPhoneStatus((prev) => ({ ...prev, enabled: false, message: msg }));
    } finally {
      setLoading(false);
    }
  };


  // Phase 7g.15.69 — Phone-as-login via Resend MAGIC LINK (no SMS).
  // Decoupled from the SMS-OTP path above which remains feature-flagged
  // behind PHONE_LOGIN_ENABLED + Twilio production unlock. This path
  // works today inside the hard locks: user enters phone, we look up
  // their email server-side and email them a click-to-login link.
  const handlePhoneMagicLink = async (e) => {
    e.preventDefault();
    const e164 = normalizeE164(formData.phone);
    if (!e164 || e164.length < 8) {
      setError(t("auth_errors.invalid_phone"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post("/auth/phone/request-magic-link", {
        phone: e164,
        user_type: "customer",
        remember_me: keepLoggedIn,
      });
      // Backend always returns a generic OK (account-enumeration
      // defence) — show the same friendly toast regardless of whether
      // the phone matched a real account.
      toast.success(
        res.data?.message ||
        t("auth.phone_magic_link_sent")
      );
      // Persist the user's remember-me choice so the magic-link landing
      // page mirrors token into localStorage correctly.
      setKeepLoggedInPref(keepLoggedIn);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message)
        || (typeof detail === "string" ? detail : null)
        || t("auth_errors.could_not_send_link");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };


  // Apple Setup Modal
  const AppleSetupModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="apple-setup-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-white mb-4">Apple Sign-In Setup Required</h3>
        <p className="text-slate-400 mb-4">
          To enable Apple Sign-In, configure the following:
        </p>
        <ol className="text-slate-300 text-sm space-y-2 mb-6 list-decimal list-inside">
          <li>Create an App ID in Apple Developer Portal</li>
          <li>Create a Service ID for web authentication</li>
          <li>Register your domain and redirect URLs</li>
          <li>Generate a private key (.p8 file)</li>
          <li>Add REACT_APP_APPLE_CLIENT_ID to your .env file</li>
        </ol>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowAppleSetup(false)}
            className="flex-1 border-white/10 text-white"
            data-testid="apple-setup-close"
          >
            Close
          </Button>
          <Button
            onClick={() => window.open("https://developer.apple.com/account/resources/identifiers/list/serviceId", "_blank")}
            className="flex-1 bg-white text-black"
          >
            Apple Developer
          </Button>
        </div>
      </div>
    </div>
  );


  // ─── Main auth selector ───
  if (mode === "main") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="auth-main-screen">
        {showAppleSetup && <AppleSetupModal />}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[128px]" />
        </div>

        <div className="relative w-full max-w-md">
          <div className="absolute -top-12 left-0 right-0 flex items-center justify-between">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-home"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>{t("common.back")}</span>
            </button>
            <div className="flex items-center gap-2">
              <ThemeToggleCompact />
              <LanguageSelector variant="compact" />
            </div>
          </div>

          <Card className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
            <CardContent className="p-8 sm:p-10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/25">
                  <Droplets className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {t("auth.welcome_title")}<sup className="text-[0.5em] ml-0.5">™</sup>
                </h1>
                <p className="text-slate-400 text-sm">The Car Wash Concierge Company</p>
              </div>

              <div className="space-y-3">
                {passkeyReady && (
                  <Button
                    onClick={handlePasskeyLogin}
                    disabled={passkeyBusy}
                    className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/30 disabled:opacity-60"
                    data-testid="passkey-login-btn"
                  >
                    {passkeyBusy ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Fingerprint className="w-5 h-5" />
                        <span className="text-left leading-tight">
                          {t("auth.continue_with_passkey")}
                          <span className="block text-[11px] font-normal opacity-80">
                            Face ID / Touch ID / Windows Hello
                          </span>
                        </span>
                      </>
                    )}
                  </Button>
                )}

                <Button
                  onClick={handleGoogleLogin}
                  className="w-full h-14 bg-white hover:bg-slate-100 text-slate-900 font-medium rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-lg"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t("auth.continue_with_google")}
                </Button>

                <Button
                  onClick={handleAppleLogin}
                  className="w-full h-14 bg-white hover:bg-slate-100 text-slate-900 font-medium rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-lg"
                  data-testid="apple-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {t("auth.continue_with_apple")}
                </Button>

                <Button
                  onClick={() => setMode("phone")}
                  variant="outline"
                  className="w-full h-14 bg-transparent border-white/10 hover:bg-white/5 text-white font-medium rounded-2xl flex items-center justify-center gap-3 transition-all duration-300"
                  data-testid="phone-login-btn"
                >
                  <Phone className="w-5 h-5 text-cyan-400" />
                  {t("auth.continue_with_phone")}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-slate-900 text-slate-500">{t("auth.or")}</span>
                  </div>
                </div>

                <Button
                  onClick={() => setMode("email_login")}
                  variant="outline"
                  className="w-full h-14 bg-transparent border-white/10 hover:bg-white/5 text-white font-medium rounded-2xl flex items-center justify-center gap-3 transition-all duration-300"
                  data-testid="email-login-btn"
                >
                  <Mail className="w-5 h-5 text-cyan-400" />
                  {t("auth.continue_with_email")}
                </Button>

                <Button
                  onClick={handleGuestContinue}
                  variant="ghost"
                  className="w-full h-12 text-slate-400 hover:text-white hover:bg-white/5 font-medium rounded-2xl flex items-center justify-center gap-2 transition-all duration-300"
                  data-testid="guest-continue-btn"
                >
                  <UserCircle className="w-5 h-5" />
                  {t("auth.continue_as_guest")}
                </Button>
              </div>

              <p className="text-center text-slate-500 text-sm mt-6">
                {t("auth.dont_have_account")}{" "}
                <button
                  onClick={() => setMode("email_signup")}
                  className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                  data-testid="switch-to-signup"
                >
                  {t("auth.sign_up")}
                </button>
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-slate-600 text-xs mt-6">
            {t("auth.agree_terms")}
          </p>
        </div>
      </div>
    );
  }


  // ─── Phone login screen ───
  if (mode === "phone") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="auth-phone-screen">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[128px]" />
        </div>
        <div className="relative w-full max-w-md">
          <button
            onClick={() => setMode("main")}
            className="absolute -top-12 left-0 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-main-from-phone"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{t("common.back")}</span>
          </button>
          <Card className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
            <CardContent className="p-8 sm:p-10">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">{t("auth.continue_with_phone")}</h1>
                {/* Phase 7g.15.69 — SMS OTP is feature-flagged off
                    until the Twilio production unlock; until then, the
                    phone tab uses an email magic-link path (Resend) so
                    customers can still sign in by phone number.  Subtitle
                    explains the email step so they're not surprised. */}
                <p className="text-slate-400 text-sm">
                  {t("auth.phone_magic_link_subtitle")}
                </p>
              </div>

              <form onSubmit={handlePhoneMagicLink} className="wp-auth-form space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-slate-400">{t("auth.phone")}</label>
                  <div className="relative">
                    <Phone className="wp-auth-icon absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="tel"
                      placeholder={t("auth.phone_placeholder")}
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      className="wp-auth-input h-14 pl-12 bg-slate-950/50 border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                      data-testid="phone-input"
                      data-auth-input="true"
                      data-auth-leading-icon="true"
                      autoComplete="tel"
                    />
                  </div>
                </div>

                {/* Phase 7g.15.69 — "Stay signed in for 30 days" opt-in.
                    Same `keepLoggedIn` state used by the email-login tab,
                    so the user's choice survives across login methods. */}
                <label
                  className="flex items-start gap-2 text-slate-300 text-sm cursor-pointer select-none"
                  data-testid="phone-keep-logged-in-label"
                >
                  <input
                    type="checkbox"
                    checked={keepLoggedIn}
                    onChange={(e) => {
                      setKeepLoggedIn(e.target.checked);
                      setKeepLoggedInPref(e.target.checked);
                    }}
                    className="h-4 w-4 mt-0.5 rounded border-white/20 bg-slate-950 text-cyan-500 focus:ring-cyan-500"
                    data-testid="phone-keep-logged-in-checkbox"
                  />
                  <span>{t("auth.remember_me_30d")}</span>
                </label>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" data-testid="phone-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-2xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all disabled:opacity-50"
                  data-testid="phone-send-magic-link-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t("auth.send_magic_link")}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-slate-500 pt-2">
                  {t("auth.phone_magic_link_footnote")}
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }


  // ─── Forgot password screen ───
  if (mode === "forgot") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="auth-forgot-screen">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[128px]" />
        </div>
        <div className="relative w-full max-w-md">
          <button
            onClick={() => { setMode("email_login"); setForgotSent(false); }}
            className="absolute -top-12 left-0 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-login-from-forgot"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{t("auth.back_to_login")}</span>
          </button>
          <Card className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
            <CardContent className="p-8 sm:p-10">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">{t("auth.forgot_title")}</h1>
                <p className="text-slate-400 text-sm">
                  {t("auth.forgot_subtitle")}
                </p>
              </div>

              {forgotSent ? (
                <div className="space-y-4" data-testid="forgot-success-state">
                  <div className="flex items-start gap-2 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-200 text-sm">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-emerald-400" />
                    <span>
                      {t("auth.forgot_sent_message")}
                    </span>
                  </div>
                  <Button
                    onClick={() => { setMode("email_login"); setForgotSent(false); }}
                    className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-2xl"
                    data-testid="forgot-back-to-login-btn"
                  >
                    {t("auth.back_to_signin")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="wp-auth-form space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">{t("auth.email")}</label>
                    <div className="relative">
                      <Mail className="wp-auth-icon absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type="email"
                        placeholder={t("auth.email_placeholder")}
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        className="wp-auth-input h-14 pl-12 bg-slate-950/50 border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        data-testid="forgot-email-input"
                        data-auth-input="true"
                        data-auth-leading-icon="true"
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-2xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all disabled:opacity-50"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>{t("auth.forgot_password")}<ArrowRight className="w-5 h-5 ml-2" /></>)}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }


  // ─── Email login / signup form ───
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="auth-email-screen">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[128px]" />
      </div>

      <div className="relative w-full max-w-md">
        <button
          onClick={() => setMode("main")}
          className="absolute -top-12 left-0 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          data-testid="back-to-main"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>{t("common.back")}</span>
        </button>

        <Card className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
          <CardContent className="p-8 sm:p-10">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {mode === "email_signup" ? t("auth.create_account") : t("auth.welcome_back")}
              </h1>
              <p className="text-slate-400 text-sm">
                {mode === "email_signup" ? t("auth.continue_with_email") : t("auth.sign_in")}
              </p>
            </div>

            <form onSubmit={handleEmailAuth} className="wp-auth-form space-y-4">
              {mode === "email_signup" && (
                <div className="space-y-2">
                  <label className="text-sm text-slate-400">{t("auth.name")}</label>
                  <div className="relative">
                    <User className="wp-auth-icon absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="text"
                      placeholder={t("auth.your_name")}
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      className="wp-auth-input h-14 pl-12 bg-slate-950/50 border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                      data-testid="name-input"
                      data-auth-input="true"
                      data-auth-leading-icon="true"
                      autoComplete="name"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm text-slate-400">{t("auth.email")}</label>
                <div className="relative">
                  <Mail className="wp-auth-icon absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="email"
                    placeholder={t("auth.email_placeholder")}
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className="wp-auth-input h-14 pl-12 bg-slate-950/50 border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    data-testid="email-input"
                    data-auth-input="true"
                    data-auth-leading-icon="true"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-400">{t("auth.password")}</label>
                <div className="relative">
                  <Lock className="wp-auth-icon absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.password_placeholder")}
                    value={formData.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    className="wp-auth-input h-14 pl-12 pr-12 bg-slate-950/50 border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    data-testid="password-input"
                    data-auth-input="true"
                    data-auth-leading-icon="true"
                    data-auth-trailing-icon="true"
                    autoComplete={mode === "email_signup" ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                    aria-label={t("auth.password")}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {mode === "email_signup" && (
                <div className="space-y-2">
                  <label className="text-sm text-slate-400">{t("auth.password")}</label>
                  <div className="relative">
                    <Lock className="wp-auth-icon absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth.password_placeholder")}
                      value={formData.confirmPassword}
                      onChange={(e) => updateField("confirmPassword", e.target.value)}
                      className="wp-auth-input h-14 pl-12 bg-slate-950/50 border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                      data-testid="confirm-password-input"
                      data-auth-input="true"
                      data-auth-leading-icon="true"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              )}

              {/* Phase 7g.15.48 — Twilio A2P CTA on customer registration.
                  UNCHECKED by default · OPTIONAL · never prechecked.
                  Only renders during email_signup. */}
              {mode === "email_signup" && (
                <button
                  type="button"
                  onClick={() => setSmsConsent((v) => !v)}
                  aria-pressed={smsConsent}
                  aria-label={t("auth_errors.aria_optional_sms_consent")}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${
                    smsConsent
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : "bg-slate-950/40 border-white/10 hover:bg-slate-950/60"
                  }`}
                  data-testid="registration-sms-consent-checkbox"
                >
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center ${
                      smsConsent ? "bg-emerald-500 border-emerald-500" : "bg-slate-900 border-white/20"
                    }`}
                  >
                    {smsConsent && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </span>
                  <span
                    className="text-[11px] text-slate-300 leading-relaxed"
                    data-testid="registration-sms-consent-label"
                  >
                    I agree to receive SMS text messages from WashPros Global Services LLC about my account, waitlist status, bookings, service updates, support requests, and operational notices. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not required to purchase services or join the waitlist. See{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-cyan-400 hover:underline"
                      data-testid="registration-sms-consent-terms-link"
                    >
                      Terms
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-cyan-400 hover:underline"
                      data-testid="registration-sms-consent-privacy-link"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </button>
              )}

              {/* Keep-me-logged-in toggle + Forgot password link */}
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-300 cursor-pointer select-none" data-testid="keep-me-logged-in-label">
                  <input
                    type="checkbox"
                    checked={keepLoggedIn}
                    onChange={(e) => {
                      setKeepLoggedIn(e.target.checked);
                      setKeepLoggedInPref(e.target.checked);
                    }}
                    className="h-4 w-4 rounded border-white/20 bg-slate-950 text-cyan-500 focus:ring-cyan-500"
                    data-testid="keep-me-logged-in-checkbox"
                  />
                  {t("auth.remember_me")}
                </label>
                {mode === "email_login" && (
                  <button
                    type="button"
                    onClick={() => { setMode("forgot"); setError(null); }}
                    className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t("auth.forgot_password")}
                  </button>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              {/* OTA 1.0.3 (2026-06-19) — Native-safe auth diagnostic surface.
                  When login fails on Android/iOS WebView, users had zero
                  visibility into the actual failure mode. Tapping the
                  "Show technical details" reveals HTTP status, endpoint
                  path, backend host, axios error code, server detail,
                  and request id — sanitized (no tokens, no PII).
                  This is the in-app equivalent of opening DevTools. */}
              {errorDetails && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5" data-testid="auth-error-details">
                  <button
                    type="button"
                    onClick={() => setShowErrorDetails((v) => !v)}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-red-300 hover:text-red-200 transition-colors flex items-center justify-between"
                    data-testid="auth-error-details-toggle"
                  >
                    <span>{showErrorDetails
                      ? t("auth_errors.hide_technical_details")
                      : t("auth_errors.show_technical_details")}</span>
                    <span className="text-red-400/60">{showErrorDetails ? "▾" : "▸"}</span>
                  </button>
                  {showErrorDetails && (
                    <div
                      className="px-3 pb-3 text-[11px] leading-relaxed font-mono text-red-200/90 break-all"
                      data-testid="auth-error-details-content"
                    >
                      <div>status: <span className="text-red-100">{errorDetails.status ?? "—"}</span></div>
                      <div>endpoint: <span className="text-red-100">{errorDetails.endpoint || "—"}</span></div>
                      <div>backend: <span className="text-red-100">{errorDetails.backendHost || "—"}</span></div>
                      {errorDetails.code && (
                        <div>code: <span className="text-red-100">{errorDetails.code}</span></div>
                      )}
                      {errorDetails.name && (
                        <div>name: <span className="text-red-100">{errorDetails.name}</span></div>
                      )}
                      {errorDetails.detail && (
                        <div>detail: <span className="text-red-100">{errorDetails.detail}</span></div>
                      )}
                      {errorDetails.requestId && (
                        <div>req-id: <span className="text-red-100">{errorDetails.requestId}</span></div>
                      )}
                      <div>ts: <span className="text-red-100">{errorDetails.ts}</span></div>
                      {/* OTA 1.0.3 — One-tap copy of the sanitized envelope.
                          Pastes the same fields the user sees (no tokens, no
                          PII, no headers).  Confirms with an inline label
                          flip (no toast — keeps the surface contained). */}
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await copyAuthDiagnostic(serializeAuthDiagnostic(errorDetails));
                          setCopyStatus(ok ? "copied" : "failed");
                          setTimeout(() => setCopyStatus("idle"), 1600);
                        }}
                        data-testid="auth-error-details-copy"
                        aria-live="polite"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-red-400/30 bg-red-500/10 px-2 py-1 text-[11px] font-sans font-medium text-red-100 hover:bg-red-500/20 active:bg-red-500/30 transition-colors"
                      >
                        {copyStatus === "copied"
                          ? t("auth_errors.copy_details_copied")
                          : copyStatus === "failed"
                            ? t("auth_errors.copy_details_failed")
                            : t("auth_errors.copy_details")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Phase 7g.15.103 — magic-link recovery surfaced when login
                  can't reach backend (network drop / 5xx). One-tap inline
                  alternative so users on flaky native networks aren't
                  stranded behind a generic "Something went wrong" toast. */}
              {(errorKind === "network" || errorKind === "server") && !magicRecoverySent && (
                <button
                  type="button"
                  onClick={handleEmailMagicLinkRecovery}
                  disabled={magicRecoveryBusy}
                  data-testid="auth-magic-link-recovery-btn"
                  className="w-full h-12 px-4 rounded-xl border border-cyan-400/40 bg-cyan-500/5 text-cyan-300 text-sm font-medium hover:bg-cyan-500/10 active:scale-[0.99] transition-all disabled:opacity-50"
                >
                  {magicRecoveryBusy ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("auth_errors.sending_magic_link")}
                    </span>
                  ) : (
                    t("auth_errors.try_magic_link_btn")
                  )}
                </button>
              )}

              {magicRecoverySent && (
                <div
                  className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-300 text-sm"
                  data-testid="auth-magic-link-recovery-sent"
                >
                  {t("auth_errors.magic_link_sent_inline")}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-2xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50"
                data-testid="submit-auth-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {mode === "email_signup" ? t("auth.create_account") : t("auth.sign_in_button")}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-slate-500 text-sm mt-6">
              {mode === "email_signup" ? (
                <>
                  {t("auth.already_have_account")}{" "}
                  <button
                    onClick={() => setMode("email_login")}
                    className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                    data-testid="switch-to-login"
                  >
                    {t("auth.sign_in")}
                  </button>
                </>
              ) : (
                <>
                  {t("auth.dont_have_account")}{" "}
                  <button
                    onClick={() => setMode("email_signup")}
                    className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                    data-testid="switch-to-signup"
                  >
                    {t("auth.sign_up")}
                  </button>
                </>
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PremiumAuthPage;
